<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, toRef } from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import AdpDocumentCardSkeleton from '@/components/common/adp-document/adp-document-card-skeleton.vue';
import { useThemeSummary } from '@/components/feed-summary/composables/use-theme-summary';
import FeedSummaryGenerationButton from '@/components/feed-summary/feed-summary-generation-button-next.vue';
import { useHomeSectionViewTracking } from '@/components/home/use-home-section-view-tracking';
import { AdpDocument, Article, Theme } from '@/types';
import { balancedFeedCounts } from '@/utils';
import { syncAdpDocumentActions } from '@/utils/adpDocument';
import { themeEditable } from '@/utils/authorization.js';
import { useAdpDocumentOptionMenu } from '@/utils/composables/useAdpDocumentOptionMenu';
import { useThemeDisabledOption } from '@/utils/composables/useThemeDisabledOption';
import { formatDate } from '@/utils/formatters';
import { useTheme, useThemeKeywordFeed } from '@/utils/swr';
import { useEmitter, useStore } from '@/utils/vue';
import AdpDocumentCardSmall from '../common/article/adp-document-card-small.vue';
import FeedSummaryNext from '../feed-summary/feed-summary-next.vue';

const props = defineProps<{
  theme: Theme;
  articleCount: number;
}>();

const layoutVariant = 'next';

const store = useStore();
const userInfo = computed(() => store.state.userInfo.userInfo);
const groups = computed(() => store.state.groups.groups);
const emitter = useEmitter();

const dateStr = formatDate(dayjs().toISOString());

const { data: themeResponse } = useTheme(props.theme.id);
const themeDetail = computed(() => themeResponse.value?.themes);
const feedType = computed(() => store.state.feedType.feedType);

const themeGroup = computed(() =>
  groups.value?.find(g => g.id === props.theme.group_id),
);

const optionMenu = useAdpDocumentOptionMenu({
  pageName: 'home',
  feature: 'themes_pickup',
  themeId: () => props.theme.id,
  themeExcludedSitesCount: () => props.theme.site_exclusion_ids?.length ?? 0,
  feedType: () => feedType.value,
});
const pickupMenuConfig = { undisplay: true, siteExclusion: true };

const removedArticles = reactive({ items: [] as number[] });

const TOTAL_VISIBLE_ARTICLE_COUNT = 8;

const { data: domesticFeed } =
  feedType.value === 'all'
    ? useThemeKeywordFeed(
        props.theme.id,
        ref('domestic'),
        undefined,
        ref(1),
        TOTAL_VISIBLE_ARTICLE_COUNT,
      )
    : { data: { value: undefined } };
const { data: foreignFeed } =
  feedType.value === 'all'
    ? useThemeKeywordFeed(
        props.theme.id,
        ref('foreign'),
        undefined,
        ref(1),
        TOTAL_VISIBLE_ARTICLE_COUNT,
      )
    : { data: { value: undefined } };

const { data } =
  feedType.value !== 'all'
    ? useThemeKeywordFeed(
        props.theme.id,
        feedType,
        undefined,
        ref(1),
        TOTAL_VISIBLE_ARTICLE_COUNT,
      )
    : { data: { value: undefined } };

const allArticles = computed(() => {
  if (feedType.value === 'all') {
    if (!domesticFeed.value || !foreignFeed.value) return undefined;
    const [domesticCount, foreignCount] = balancedFeedCounts(
      domesticFeed.value.keyword_feeds.length,
      foreignFeed.value.keyword_feeds.length,
      TOTAL_VISIBLE_ARTICLE_COUNT,
    );
    return [
      ...domesticFeed.value.keyword_feeds.slice(0, domesticCount),
      ...foreignFeed.value.keyword_feeds.slice(0, foreignCount),
    ];
  } else {
    return data.value ? data.value.keyword_feeds : undefined;
  }
});

const articles = computed(() =>
  allArticles.value?.filter(a => !removedArticles.items.includes(a.id)),
);

const hasArticles = computed(() => (articles.value ?? []).length > 0);

const {
  summary,
  generateButtonState,
  generateSummary,
  shouldShowSummary,
  mutate: mutateThemeSummary,
} = useThemeSummary(
  {
    page_type: 'feed',
    feed_type: feedType.value,
    model_type: 'article',
    date: dayjs(props.theme.feed_last_updated_at).format('YYYY-MM-DD'),
    theme_id: props.theme.id,
  },
  toRef(props, 'articleCount'),
  'home',
);

const isLoaded = computed(
  () => articles.value !== undefined && themeDetail.value !== undefined,
);
const isPreparingFeed = computed(
  () => themeDetail.value?.feed_last_updated_at === null,
);

const { setRef, requestId } = useHomeSectionViewTracking(
  'themes_pickup_view',
  isLoaded,
  () => ({
    document_count: articles.value?.length ?? 0,
    theme: { id: props.theme.id },
    layout_variant: layoutVariant,
  }),
);

const { disabledOption } = useThemeDisabledOption({
  theme: props.theme,
  themeFeedType: feedType.value,
  docType: 'article',
});
const isForeignMediaDisplayOnDomesticTheme = computed(
  () => disabledOption?.name === '海外メディア',
);
const isDomesticMediaDisplayOnChineseTheme = computed(
  () => props.theme.is_chinese && feedType.value === 'domestic',
);

const onArticleRemoved = ({
  themeId,
  articleId,
}: {
  themeId: Theme['id'];
  articleId: Article['id'];
}) => {
  if (themeId === props.theme.id) {
    removedArticles.items.push(articleId);
    mutateThemeSummary();
  }
};
const syncArticle = (updated: AdpDocument) =>
  syncAdpDocumentActions(articles.value ?? [], updated);
onMounted(() => {
  emitter.on('article-removed', onArticleRemoved);
  emitter.on('article-updated', syncArticle);
  emitter.on('article-viewed', syncArticle);
});
onUnmounted(() => {
  emitter.off('article-removed', onArticleRemoved);
  emitter.off('article-updated', syncArticle);
  emitter.off('article-viewed', syncArticle);
});
</script>
<template>
  <div class="theme-news" :ref="setRef">
    <div class="header">
      <div class="theme-info">
        <span class="title">
          <DgrIcon name="star-fill" size="small" :keep-fill="false" />
          <span class="c-title-bold--s">お気に入りテーマ</span>
          <span class="c-text-regular--s date">{{ dateStr }}</span>
        </span>
        <span class="theme-name">
          <router-link
            :to="{ name: 'themeFeed', params: { themeId: String(theme.id) } }"
            class="c-title-bold--xm"
            >{{ theme.name }}</router-link
          >
          <span class="c-text-regular--s count">{{
            articleCount > 0 ? `${articleCount}件` : ''
          }}</span>
        </span>
      </div>
      <FeedSummaryGenerationButton
        v-if="!theme.is_chinese && articles && articles.length > 0"
        :state="generateButtonState"
        class="generation-button"
        @click="generateSummary()"
      />
    </div>
    <FeedSummaryNext
      v-if="shouldShowSummary(summary)"
      :feed-summary="summary"
      class="feed-summary"
      feature="keyword_feed"
      page-name="home"
      :theme-id="theme.id"
    />
    <div class="documents" v-if="hasArticles">
      <AdpDocumentCardSmall
        v-for="article in articles"
        :key="article.id"
        :id="`${theme.id}-${article.id}`"
        class="document-card"
        :adp-document="article"
        :theme-id="theme.id"
        :feed-type="feedType"
        show-published-date
        show-option-menu
        :option-actions="
          optionMenu.buildOptionActions(article, pickupMenuConfig)
        "
        page-name="home"
        :feature="'themes_pickup'"
        @option-action="name => optionMenu.handleOptionAction(name, article)"
        :contents-context="{
          event_name: 'themes_pickup',
          parent_request_id: requestId,
        }"
      />
    </div>
    <template v-else-if="isLoaded">
      <div v-if="isPreparingFeed" class="preparing">
        <img src="@/assets/loading2.gif" class="loading-img" />
        <div class="message">
          <div class="c-title-medium--xm title">
            このテーマの最新情報を集めています
          </div>
          <div class="c-text-regular--s">
            <div>完了までおよそ5〜10分。このまま閉じても大丈夫です。</div>
            <div>準備ができたら「お知らせ」に届きます。</div>
          </div>
          <div class="c-text-regular--xs">
            <div>初回のあとは毎朝7:00に更新されます。</div>
          </div>
        </div>
      </div>
      <div v-else class="empty">
        <img src="@/assets/state-empty-article.png" class="empty-img" />
        <template
          v-if="
            isForeignMediaDisplayOnDomesticTheme ||
            isDomesticMediaDisplayOnChineseTheme
          "
        >
          <div class="c-title-medium--xm title">
            設定により、このテーマの記事が表示されていません
          </div>
          <div class="c-text-regular--s">
            {{
              isDomesticMediaDisplayOnChineseTheme
                ? 'このテーマは中国語メディアの記事のみを扱っています。現在のメディアの表示設定が「国内メディアのみ」のため、該当する記事がありません'
                : 'このテーマは国内メディアの記事のみを扱っています。現在のメディアの表示設定が「海外メディアのみ」のため、該当する記事がありません'
            }}
          </div>
          <div class="c-text-regular--s">
            メディアの表示設定を「{{
              isDomesticMediaDisplayOnChineseTheme ? '海外' : '国内'
            }}メディアのみ」または「国内・海外メディア」に変更すると、このテーマの記事が表示されます。
          </div>
        </template>
        <template v-else>
          <div class="c-title-medium--xm title">
            このテーマの条件に合う記事はありませんでした
          </div>
          <div class="c-text-regular--s">次回の更新は明日7:00です。</div>
          <div class="c-text-regular--xs">
            <template v-if="themeEditable(userInfo, theme, themeGroup)">
              <div>
                現時点では、テーマに関連する十分な情報が見つかりませんでした。
              </div>
              <div>次回の更新で新しい記事が追加される可能性があります</div>
            </template>
            <template v-else>
              キーワードが絞り込まれすぎている可能性があります
            </template>
          </div>
        </template>
      </div>
    </template>
    <div v-else class="documents">
      <AdpDocumentCardSkeleton
        :loading="true"
        size="s"
        class="document-card skeleton"
      />
      <AdpDocumentCardSkeleton
        :loading="true"
        size="s"
        class="document-card skeleton"
      />
    </div>
    <div v-if="!isPreparingFeed" class="footer">
      <template
        v-if="
          isForeignMediaDisplayOnDomesticTheme ||
          isDomesticMediaDisplayOnChineseTheme
        "
      >
        <router-link
          :to="{
            name:
              userInfo?.role === 'admin'
                ? 'adminMediaDisplay'
                : 'settingsMediaDisplay',
          }"
          class="c-btn c-outlineBtn c-outlineBtn--auto c-outlineBtn--small"
        >
          メディアの表示設定を開く
        </router-link>
      </template>
      <template v-else-if="isLoaded && !hasArticles">
        <router-link
          v-if="themeEditable(userInfo, theme, themeGroup)"
          :to="{ name: 'themeEdit', params: { themeId: theme.id } }"
          class="c-btn c-outlineBtn c-outlineBtn--auto c-outlineBtn--small"
        >
          キーワードを見直す
        </router-link>
        <router-link
          :to="{ name: 'themeFeed', params: { themeId: theme.id } }"
          class="c-btn c-outlineBtn c-outlineBtn--auto c-outlineBtn--small"
        >
          過去の配信を見る
        </router-link>
      </template>
      <router-link
        v-else
        :to="{ name: 'themeFeed', params: { themeId: theme.id } }"
        class="c-btn c-outlineBtn c-outlineBtn--auto c-outlineBtn--small"
      >
        このテーマをすべて見る
      </router-link>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.theme-news {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  background: #ffffff;

  .header {
    display: flex;
    justify-content: space-between;

    .theme-info {
      display: flex;
      flex-direction: column;
      gap: 2px;

      .title {
        display: flex;
        gap: 2px;
        align-items: center;
        color: $color-green600;

        .date {
          margin-left: 6px;
          color: $color-gray800;
        }
      }

      .theme-name {
        display: flex;
        gap: 8px;
        align-items: center;

        a {
          display: -webkit-box;
          -webkit-box-orient: vertical;
          line-clamp: 2;
          -webkit-line-clamp: 2;
          overflow: hidden;
          &:hover {
            color: $color-green600;
            text-decoration: underline;
          }
        }

        .count {
          color: $color-gray800;
        }
      }
    }
  }

  .documents {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    .document-card {
      min-width: 0;
      padding: 16px;
      border: 1px solid $color-gray400;
      border-radius: 4px;

      &:has(.document-info-image a:hover) {
        border-color: $color-gray600;
      }
    }
  }

  .preparing {
    display: flex;
    flex-direction: column;
    align-items: center;

    .loading-img {
      width: 120px;
      height: 120px;
    }
    .message {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      margin-bottom: 8px;

      .c-title-medium--xm {
        margin-bottom: 12px;
      }
      .c-text-regular--s,
      .c-text-regular--xs {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
    }
  }

  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    max-width: 480px;
    margin: 0 auto 8px auto;
    text-align: center;

    .empty-img {
      width: 132px;
      height: 80px;
      object-fit: cover;
    }
    .c-title-medium--xm {
      margin-bottom: 8px;
    }
  }

  .footer {
    display: flex;
    justify-content: center;
    gap: 16px;
  }

  .empty + .footer {
    margin-bottom: 8px;
  }
}
</style>
